import { useCallback, useMemo, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  justifyContentSpaceBetween,
} from 'shared/styles/commonStyles';
import { colors } from 'shared/styles/theme';
import { useLocations } from 'shared/hooks';

const STATUS_DOT = {
  fault: { bg: colors.red500, shadow: `0 0 8px ${colors.red500}` },
  online: { bg: colors.green500, shadow: `0 0 8px ${colors.green400}` },
  offline: { bg: 'var(--switch-control-track)', shadow: 'none' },
};

const SORT_MODE = {
  STATUS: 'status',
  NAME: 'name',
  COUNT: 'count',
};

const STATUS_PRIORITY = {
  fault: 0,
  online: 1,
  offline: 2,
};

const getLocationStatus = (flatSwitch, switchData, locationId, specLocationId) => {
  if (specLocationId) {
    const hasFault = Object.values(flatSwitch[specLocationId] || {}).some((m) => m.isFaults);
    if (hasFault) return 'fault';
    const hasOnline = Object.values(flatSwitch[specLocationId] || {}).some((m) => m.isWifi);
    return hasOnline ? 'online' : 'offline';
  }
  if (switchData[locationId]?.isSpecificLocation) {
    const hasFault = Object.keys(switchData[locationId].subLocations).some((specId) =>
      Object.values(flatSwitch[specId] || {}).some((m) => m.isFaults)
    );
    if (hasFault) return 'fault';
    const hasOnline = Object.keys(switchData[locationId].subLocations).some((specId) =>
      Object.values(flatSwitch[specId] || {}).some((m) => m.isWifi)
    );
    return hasOnline ? 'online' : 'offline';
  }
  const hasFault = Object.values(flatSwitch[locationId] || {}).some((m) => m.isFaults);
  if (hasFault) return 'fault';
  const hasOnline = Object.values(flatSwitch[locationId] || {}).some((m) => m.isWifi);
  return hasOnline ? 'online' : 'offline';
};

const MapPinIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path
      d="M5 5.417a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5Z"
      stroke={colors.slate600}
      strokeWidth="0.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5 9.167c1.667-1.667 3.333-3.15 3.333-4.817a3.333 3.333 0 1 0-6.666 0c0 1.667 1.666 3.15 3.333 4.817Z"
      stroke={colors.slate600}
      strokeWidth="0.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRightIcon = ({ rotated }) => (
  <ChevronWrapper $rotated={rotated}>
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path
        d="M5.25 10.5 8.75 7l-3.5-3.5"
        stroke={colors.slate500}
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </ChevronWrapper>
);

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M12.25 12.25 9.4 9.4M10.917 6.417a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z"
      stroke={colors.slate500}
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SortIcon = ({ mode }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M3.5 2.917v8.166m0 0-1.75-1.75m1.75 1.75L5.25 9.333"
      stroke={mode === SORT_MODE.STATUS ? colors.blue500 : colors.slate500}
      strokeWidth="1.1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.75 4.083h3.5M8.75 7h2.333M8.75 9.917h1.167"
      stroke={mode === SORT_MODE.COUNT ? colors.blue500 : colors.slate500}
      strokeWidth="1.1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle
      cx="8.167"
      cy="4.083"
      r="0.583"
      fill={mode === SORT_MODE.NAME ? colors.blue500 : colors.slate500}
    />
  </svg>
);

const getLocationLabel = (locData, fallbackId) =>
  locData.location_name || locData.locationName || fallbackId;

const getCompanyLabel = (locData) =>
  locData.company_name || locData.companyName || '';

const getAddressLabel = (locData) =>
  [locData.zone_city, locData.zone_state_short_name].filter(Boolean).join(', ');

const getSearchText = (...parts) =>
  parts
    .flat()
    .filter(Boolean)
    .join(' ')
    .toUpperCase();

const LocationSidebar = ({
  swtName,
  switchData,
  flatSwitch,
  selectedLocation,
  selectedSpecificLocation,
  onSelect,
}) => {
  const locations = useLocations();
  const [searchValue, setSearchValue] = useState('');
  const [sortMode, setSortMode] = useState(SORT_MODE.STATUS);
  const [expandedLocations, setExpandedLocations] = useState({});

  const toggleExpanded = useCallback((locationId) => {
    setExpandedLocations((prev) => ({
      ...prev,
      [locationId]: !prev[locationId],
    }));
  }, []);

  const normalizedQuery = searchValue.trim().toUpperCase();


  const locationEntries = useMemo(() => {
    const getSwitchCount = (locationId, specLocationId = null) => {
      if (specLocationId) {
        return Object.keys(
          switchData[locationId]?.subLocations?.[specLocationId]?.devices || {}
        ).length;
      }
      if (switchData[locationId]?.isSpecificLocation) {
        return Object.values(switchData[locationId].subLocations).reduce(
          (acc, spec) => acc + Object.keys(spec.devices || {}).length,
          0
        );
      }
      return Object.keys(switchData[locationId]?.devices || {}).length;
    };

    const sortEntries = (entries) => {
      const list = [...entries];

      return list.sort((entryA, entryB) => {
        if (sortMode === SORT_MODE.NAME) {
          return entryA.label.localeCompare(entryB.label);
        }

        if (sortMode === SORT_MODE.COUNT) {
          return (
            entryB.switchCount - entryA.switchCount ||
            entryA.label.localeCompare(entryB.label)
          );
        }

        return (
          STATUS_PRIORITY[entryA.status] - STATUS_PRIORITY[entryB.status] ||
          entryB.switchCount - entryA.switchCount ||
          entryA.label.localeCompare(entryB.label)
        );
      });
    };

    const entries = Object.keys(switchData).map((locationId) => {
      const locData = locations?.[swtName]?.[locationId] || {};
      const isSpecificLocation = switchData[locationId]?.isSpecificLocation;
      const label = getLocationLabel(locData, locationId);
      const company = getCompanyLabel(locData);
      const address = getAddressLabel(locData);
      const status = getLocationStatus(flatSwitch, switchData, locationId, null);
      const switchCount = getSwitchCount(locationId);

      const subLocations = isSpecificLocation
        ? Object.keys(switchData[locationId].subLocations).map((specId) => {
            const specData = locations?.[swtName]?.[specId] || {};
            const specLabel =
              specData.specific_address ||
              specData.location_name ||
              specData.locationName ||
              specId;
            const specCompany = getCompanyLabel(specData) || company;
            const specAddress = getAddressLabel(specData);

            return {
              specId,
              label: specLabel,
              company: specCompany,
              address: specAddress,
              status: getLocationStatus(flatSwitch, switchData, locationId, specId),
              switchCount: getSwitchCount(locationId, specId),
              searchText: getSearchText(specLabel, specCompany, specAddress),
            };
          })
        : [];

      const matchesParent = getSearchText(label, company, address).includes(
        normalizedQuery
      );
      const visibleSubLocations = normalizedQuery
        ? sortEntries(
            subLocations.filter(
              (subLocation) =>
                matchesParent || subLocation.searchText.includes(normalizedQuery)
            )
          )
        : sortEntries(subLocations);

      if (normalizedQuery && !matchesParent && !visibleSubLocations.length) {
        return null;
      }

      return {
        locationId,
        label,
        company,
        address,
        status,
        switchCount,
        isSpecificLocation,
        subLocationCount: subLocations.length,
        visibleSubLocations,
      };
    });

    return sortEntries(entries.filter(Boolean));
  }, [flatSwitch, locations, normalizedQuery, sortMode, swtName, switchData]);

  const cycleSortMode = () => {
    setSortMode((currentMode) => {
      if (currentMode === SORT_MODE.STATUS) return SORT_MODE.NAME;
      if (currentMode === SORT_MODE.NAME) return SORT_MODE.COUNT;
      return SORT_MODE.STATUS;
    });
  };

  const sortLabel =
    sortMode === SORT_MODE.STATUS
      ? 'STATUS'
      : sortMode === SORT_MODE.NAME
      ? 'NAME'
      : 'COUNT';

  return (
    <SidebarWrapper>
      <SidebarHeader>
        <HeaderCopy>
          <HeaderLabel>LOCATIONS OVERVIEW</HeaderLabel>
          <HeaderCount>{locationEntries.length}</HeaderCount>
        </HeaderCopy>
        <ToolbarRow>
          <SearchField>
            <SearchFieldIcon>
              <SearchIcon />
            </SearchFieldIcon>
            <SearchInput
              type="text"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Search zones, companies, cities..."
              aria-label="Search locations overview"
            />
            {searchValue && (
              <ClearSearchButton
                type="button"
                onClick={() => setSearchValue('')}
                aria-label="Clear location search"
              >
                Clear
              </ClearSearchButton>
            )}
          </SearchField>
          <SortButton
            type="button"
            onClick={cycleSortMode}
            title={`Sort locations by ${sortLabel.toLowerCase()}`}
            aria-label={`Sort locations by ${sortLabel.toLowerCase()}`}
          >
            <SortIcon mode={sortMode} />
            <SortLabel>{sortLabel}</SortLabel>
          </SortButton>
        </ToolbarRow>
      </SidebarHeader>

      <LocationList>
        {locationEntries.length ? (
          locationEntries.map((entry) => {
            const {
              locationId,
              label,
              company,
              address,
              status,
              switchCount,
              isSpecificLocation,
              subLocationCount,
              visibleSubLocations,
            } = entry;

            if (isSpecificLocation) {
              const anyChildSelected = visibleSubLocations.some(
                (subLocation) => selectedSpecificLocation === subLocation.specId
              );
              const isExpanded =
                expandedLocations[locationId] || anyChildSelected;

              return (
                <TreeGroup key={locationId}>
                  <NavCard
                    type="button"
                    $isSelected={anyChildSelected || (selectedLocation === locationId && !selectedSpecificLocation)}
                    onClick={() => {
                      toggleExpanded(locationId);
                      onSelect(locationId, null);
                    }}
                  >
                    <TopRow>
                      <NameGroup>
                        <StatusDot $status={status} />
                        <LocationName $isSelected={anyChildSelected || (selectedLocation === locationId && !selectedSpecificLocation)}>
                          {label}
                        </LocationName>
                      </NameGroup>
                      <LocBadge $hasFault={status === 'fault'}>
                        {subLocationCount} SUB
                      </LocBadge>
                    </TopRow>
                    <BottomRow>
                      <InfoGroup>
                        {company && <CompanyText>{company}</CompanyText>}
                        {address && (
                          <AddressRow>
                            <MapPinIcon />
                            <AddressText>{address}</AddressText>
                          </AddressRow>
                        )}
                      </InfoGroup>
                      <ChevronRightIcon rotated={isExpanded} />
                    </BottomRow>
                  </NavCard>

                  {isExpanded && (
                    <ChildList>
                      {visibleSubLocations.map((subLoc) => {
                        const isChildSelected =
                          selectedSpecificLocation === subLoc.specId;

                        return (
                          <ChildCard
                            key={subLoc.specId}
                            type="button"
                            $isSelected={isChildSelected}
                            onClick={() => onSelect(locationId, subLoc.specId)}
                          >
                            <TopRow>
                              <NameGroup>
                                <StatusDot $status={subLoc.status} $small />
                                <ChildName $isSelected={isChildSelected}>
                                  {subLoc.label}
                                </ChildName>
                              </NameGroup>
                              <LocBadge $hasFault={subLoc.status === 'fault'}>
                                {subLoc.switchCount}
                              </LocBadge>
                            </TopRow>
                            {subLoc.company && (
                              <ChildInfo>{subLoc.company}</ChildInfo>
                            )}
                          </ChildCard>
                        );
                      })}
                    </ChildList>
                  )}
                </TreeGroup>
              );
            }

            const isSelected =
              selectedLocation === locationId && !selectedSpecificLocation;

            return (
              <NavCard
                key={locationId}
                type="button"
                $isSelected={isSelected}
                onClick={() => onSelect(locationId, null)}
              >
                <TopRow>
                  <NameGroup>
                    <StatusDot $status={status} />
                    <LocationName $isSelected={isSelected}>{label}</LocationName>
                  </NameGroup>
                  <LocBadge $hasFault={status === 'fault'}>
                    {switchCount} LOC
                  </LocBadge>
                </TopRow>
                <BottomRow>
                  <InfoGroup>
                    {company && <CompanyText>{company}</CompanyText>}
                    {address && (
                      <AddressRow>
                        <MapPinIcon />
                        <AddressText>{address}</AddressText>
                      </AddressRow>
                    )}
                  </InfoGroup>
                  <ChevronRightIcon />
                </BottomRow>
              </NavCard>
            );
          })
        ) : (
          <EmptyState>
            <EmptyStateTitle>No matching locations</EmptyStateTitle>
            <EmptyStateText>Try a zone name, company, or city.</EmptyStateText>
          </EmptyState>
        )}
      </LocationList>
    </SidebarWrapper>
  );
};

export default LocationSidebar;

const SidebarWrapper = styled.aside`
  width: 280px;
  min-width: 280px;
  height: 100%;
  background: var(--switch-panel-bg);
  border: 0.8px solid var(--switch-border);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: background 0.3s ease, border-color 0.3s ease;
`;

const SidebarHeader = styled.div`
  padding: 14px 14px 12px;
  border-bottom: 1px solid var(--switch-border);
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;
`;

const HeaderCopy = styled.div`
  ${justifyContentSpaceBetween}
`;

const HeaderLabel = styled.h2`
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--switch-text-primary);
`;

const HeaderCount = styled.span`
  min-width: 26px;
  height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--switch-blue-soft);
  border: 1px solid var(--switch-blue-border);
  color: #2563eb;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchField = styled.div`
  flex: 1;
  min-width: 0;
  height: 40px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px 0 10px;
  border-radius: 12px;
  background: var(--switch-input-bg);
  border: 0.8px solid var(--switch-border);
  transition: background 0.3s ease, border-color 0.3s ease;
`;

const SearchFieldIcon = styled.span`
  width: 14px;
  height: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  color: var(--switch-text-primary);
  font-size: 11px;
  font-weight: 500;
  outline: none;

  &::placeholder {
    color: var(--switch-text-faint);
  }
`;

const ClearSearchButton = styled.button`
  height: 26px;
  padding: 0 10px;
  border: none;
  border-radius: 999px;
  background: var(--switch-button-bg);
  color: var(--switch-text-muted);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    color: var(--switch-text-primary);
    background: var(--switch-button-hover-bg);
  }
`;

const SortButton = styled.button`
  width: 72px;
  height: 40px;
  border: 0.8px solid var(--switch-border);
  border-radius: 12px;
  background: var(--switch-input-bg);
  color: var(--switch-text-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease, transform 0.2s ease, background 0.3s ease;

  &:hover {
    border-color: var(--switch-blue-border);
    color: var(--switch-text-primary);
    transform: translateY(-1px);
  }
`;

const SortLabel = styled.span`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
`;

const LocationList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px 8px 12px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--switch-scrollbar-thumb);
    border-radius: 2px;
  }
`;

const NavCard = styled.button`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: ${({ $isNested }) => ($isNested ? '8px 12px' : '13px 17px')};
  border-radius: 14px;
  cursor: pointer;
  border: 0.8px solid transparent;
  background: transparent;
  text-align: left;
  transition: all 0.2s ease;
  position: relative;

  ${({ $isSelected }) =>
    $isSelected
      ? css`
          background: var(--switch-blue-subtle);
          box-shadow: var(--switch-shadow-soft);
          border-color: var(--switch-blue-border);

          &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 12px;
            bottom: 12px;
            width: 3px;
            border-radius: 999px;
            background: linear-gradient(180deg, #60a5fa 0%, #2563eb 100%);
          }
        `
      : css`
          &:hover {
            background: var(--switch-panel-bg-alt);
            border-color: var(--switch-border);
          }
        `}
`;

const TopRow = styled.div`
  ${justifyContentSpaceBetween}
`;

const NameGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  overflow: hidden;
`;

const StatusDot = styled.span`
  width: ${({ $small }) => ($small ? '5px' : '6px')};
  height: ${({ $small }) => ($small ? '5px' : '6px')};
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $status }) => STATUS_DOT[$status]?.bg || STATUS_DOT.offline.bg};
  box-shadow: ${({ $status }) => STATUS_DOT[$status]?.shadow || STATUS_DOT.offline.shadow};
`;

const LocationName = styled.span`
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.35px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $isSelected }) => ($isSelected ? 'var(--switch-text-primary)' : 'var(--switch-text-secondary)')};
  transition: color 0.15s;
`;

const LocBadge = styled.span`
  flex-shrink: 0;
  padding: 2px 6px;
  border-radius: 8px;
  font-family: 'Consolas', monospace;
  font-size: 10px;
  line-height: 15px;
  white-space: nowrap;

  ${({ $hasFault }) =>
    $hasFault
      ? css`
          background: rgba(239, 68, 68, 0.1);
          color: ${colors.red500};
        `
      : css`
          background: var(--switch-panel-bg-alt);
          color: var(--switch-text-faint);
        `}
`;

const BottomRow = styled.div`
  ${justifyContentSpaceBetween}
  padding-left: 14px;

  ${({ $nested }) =>
    $nested &&
    css`
      padding-left: 28px;
      padding-top: 4px;
      justify-content: flex-start;
    `}
`;

const InfoGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
`;

const CompanyText = styled.span`
  font-weight: 500;
  font-size: 11px;
  line-height: 16.5px;
  color: var(--switch-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AddressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const AddressText = styled.span`
  font-weight: 400;
  font-size: 10px;
  line-height: 15px;
  color: var(--switch-text-faint);
  white-space: nowrap;
`;

const ChevronWrapper = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: transform 0.22s ease;

  ${({ $rotated }) =>
    $rotated &&
    css`
      transform: rotate(90deg);
    `}
`;


const childSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const TreeGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const ChildList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 0 4px 16px;
  margin-left: 12px;
  border-left: 1px solid var(--switch-border);
  animation: ${childSlideIn} 200ms ease;
`;

const ChildCard = styled.button`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  border: 0.8px solid transparent;
  background: transparent;
  text-align: left;
  transition: all 0.2s ease;

  ${({ $isSelected }) =>
    $isSelected
      ? css`
          background: var(--switch-blue-subtle);
          border-color: var(--switch-blue-border);
          box-shadow: var(--switch-shadow-soft);
        `
      : css`
          &:hover {
            background: var(--switch-panel-bg-alt);
            border-color: var(--switch-border);
          }
        `}
`;

const ChildName = styled.span`
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: -0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $isSelected }) =>
    $isSelected ? 'var(--switch-text-primary)' : 'var(--switch-text-secondary)'};
  transition: color 0.15s;
`;

const ChildInfo = styled.span`
  font-size: 10px;
  color: var(--switch-text-faint);
  padding-left: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmptyState = styled.div`
  min-height: 120px;
  margin: 8px 4px 0;
  padding: 20px 16px;
  border-radius: 16px;
  border: 1px dashed var(--switch-scrollbar-thumb);
  background: var(--switch-empty-bg);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
`;

const EmptyStateTitle = styled.span`
  color: var(--switch-text-primary);
  font-size: 12px;
  font-weight: 600;
`;

const EmptyStateText = styled.span`
  color: var(--switch-text-faint);
  font-size: 10px;
  line-height: 1.5;
`;
